import React, { useEffect, useRef } from 'react';
import { useAppSettings } from '../contexts/AppSettingsContext';

interface VideoProps {
    className: string;
    src: string;
    type: string;
    shouldRestart?: boolean;
    restartAfterDuration?: number;
    totalVideoDuration?: number;
    isComplete?: boolean;
    onAnimationComplete?: () => void;
}

const VideoPlayer: React.FC<VideoProps> = ({
    className, src, type, shouldRestart = false, restartAfterDuration = 3000,
    totalVideoDuration, isComplete = false, onAnimationComplete
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    const { settings, appSettingsReady } = useAppSettings();

    const shouldAutoPlay = appSettingsReady && !settings.devDisableAutoplayVideo;
    
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const handleTimeUpdate = () => {
            if (isComplete) {
                if (totalVideoDuration && (videoElement.currentTime * 1000) >= totalVideoDuration) {
                    videoElement.pause();
                    onAnimationComplete?.();
                }
            } else if (shouldRestart) {
                if (restartAfterDuration && (videoElement.currentTime * 1000) >= restartAfterDuration) {
                    videoElement.currentTime = 0;
                    videoElement.play().catch(error => console.error("Error attempting to play video:", error));
                }
            }
        };

        videoElement.addEventListener('timeupdate', handleTimeUpdate);

        return () => {
            if (videoElement) {
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
            }
        };
    }, [isComplete, onAnimationComplete, restartAfterDuration, shouldRestart, totalVideoDuration]);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement && isComplete) {
            videoElement.currentTime = 0;
            videoElement.play().catch(e => console.error("Error playing video on completion:", e));
        }
    }, [isComplete]);

    return (
        <video
            ref={videoRef}
            autoPlay={shouldAutoPlay}
            muted
            className={className}
        >
            <source src={src} type={type} />
            Your browser does not support the video tag.
        </video>
    );
};

export default VideoPlayer;
